<template>
  <modal-wrap :name="modalName" size="md" :hide-footer="true">
    <template #modal-title> Tax Numbers </template>
    <template #default>
      <VeeForm>
        <div class="edit-customer-data">
          <div v-if="!taxNumbers.length">
            <p class="center-text">No tax numbers found for this company.</p>
          </div>

          <div v-else class="tax-list">
            <div v-for="(tn, idx) in taxNumbers" :key="tn.id || idx" class="d-flex align-items-end mb-2">
              <div class="flex-grow-1 d-flex">
                <div class="mr-2" style="width: 40%">
                  <label class="tax-field-label">
                    <span>Country</span>
                    <CountryPicker v-model="tn.countryCode" :value="tn.countryCode" :name="`country_${tn.id || idx}`" placeholder="Country" />
                  </label>
                </div>
                <div class="flex-grow-1">
                  <label class="tax-field-label" :for="`tax_${tn.id || idx}`"><span>Tax number</span></label>
                  <input :id="`tax_${tn.id || idx}`" v-model="tn.taxNumber" :name="`tax_${tn.id || idx}`" class="form-control" placeholder="Tax number" type="text" />
                </div>
              </div>

              <div class="ml-2 d-flex">
                <b-button size="sm" variant="danger" :disabled="deletingMap[tn.id]" @click="deleteTaxNumber(tn)">
                  <span v-if="deletingMap[tn.id]">Deleting...</span>
                  <span v-else>Delete</span>
                </b-button>
              </div>
            </div>
          </div>

          <div class="mt-3 d-flex justify-content-end">
            <b-button size="md" class="btn-grey mr-3" variant="outline-secondary" @click="handleCancel">Cancel</b-button>
            <b-button size="md" class="btn-green" variant="success" :disabled="!companyId" @click="handleSubmit">Save changes</b-button>
            <b-button size="md" class="btn-green ml-2" variant="outline-secondary" @click="addNew">Add Tax Number</b-button>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { TaxNumber } from '@/types/Models/TaxNumber'
import { BButton } from 'bootstrap-vue-next'
import { storeToRefs } from 'pinia'
import { Form as VeeForm } from 'vee-validate'
import { defineComponent, computed, onMounted, watch, type ComputedRef } from 'vue'
import { EDIT_COMPANY_TAX_NUMBERS_MODAL } from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { useCustomersTaxNumbersStore } from '@/store/customers/taxNumbers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'

export default defineComponent({
  name: 'EditCompanyTaxNumbersModal',
  components: { ModalWrap, CountryPicker, BButton, VeeForm },
  setup() {
    const modalStore = useVueNextModalStore()
    const taxNumbersStore = useCustomersTaxNumbersStore()
    const modalName = EDIT_COMPANY_TAX_NUMBERS_MODAL
    const companyId: ComputedRef<string> = computed(() => modalStore.modalPropsByName(modalName)?.id)

    const taxNumbers = computed(() => taxNumbersStore.taxNumbersWithUi as TaxNumber[])

    // use store getters mapped to refs so template is reactive and logic lives in store
    const { savingMap, deletingMap, isNewMap } = storeToRefs(taxNumbersStore)

    const load = async () => {
      if (!companyId.value) return
      await taxNumbersStore.fetch(companyId.value).catch(none)
    }

    // reload when companyId changes (modal props may be set after mount)
    watch(companyId, (val) => {
      if (val) load()
    })

    const addNew = () => {
      taxNumbersStore.addLocal()
    }

    const isValid = (tn: TaxNumber) => {
      return !!(tn.countryCode && tn.taxNumber && tn.taxNumber.toString().trim().length > 0)
    }

    const handleSubmit = async () => {
      // collect items and decide create/update/delete
      const items = taxNumbers.value || []
      if (!companyId.value) return
      for (const item of items) {
        const id = item.id as string | undefined
        const hasValue = !!(item.taxNumber && item.taxNumber.toString().trim().length > 0)
        if (!id || String(id).startsWith('tmp_')) {
          // new item
          if (hasValue) {
            await taxNumbersStore.createLocal(companyId.value, { countryCode: item.countryCode, taxNumber: item.taxNumber }, id)
          } else {
            // nothing to do for empty new row
          }
        } else {
          // existing item
          if (hasValue) {
            await taxNumbersStore.updateLocal(companyId.value, id, { countryCode: item.countryCode, taxNumber: item.taxNumber })
          } else {
            // empty value -> delete
            await taxNumbersStore.destroyLocal(companyId.value, id)
          }
        }
      }
      // close modal after batch ops
      modalStore.closeModal(modalName)
    }

    const handleCancel = async () => {
      // revert changes by reloading server state and close
      if (companyId.value) await taxNumbersStore.fetch(companyId.value).catch(none)
      modalStore.closeModal(modalName)
    }

    const deleteTaxNumber = async (tn: TaxNumber) => {
      // delegate to store (store handles notifications and fetch)
      await taxNumbersStore.deleteTaxNumber(companyId.value, tn.id as string | undefined)
    }

    onMounted(load)

    return {
      modalName,
      taxNumbers,
      deletingMap,
      savingMap,
      isNewMap,
      isValid,
      addNew,
      handleSubmit,
      handleCancel,
      deleteTaxNumber,
      companyId,
    }
  },
})
</script>

<style scoped>
.tax-field-label {
  display: block;
  margin: 0;
  color: #59635e;
  font-size: 13px;
}
.tax-field-label > span {
  display: block;
  margin-bottom: 6px;
}
.tax-field-label :deep(.mb-3) {
  margin-bottom: 0 !important;
}
</style>
